// src/services/api.js
const API_BASE_URL = window.ENV?.API_BASE_URL || 'http://localhost:8000/api';

// Maps new spec dimension names → old backend names where they differ
const DIM_NAME_MAP = { permission_set: 'perm_hash' };
const DIM_NAME_REVERSE_MAP = { perm_hash: 'permission_set' };

function mapDimensions(dims) {
  return dims.map(d => DIM_NAME_MAP[d] || d);
}

// Converts old backend response { nodes[].sha256, edges[].type } to spec format
// { nodes[].id, nodes[].is_seed, edges[].dimensions[] }
function normalizeGraphResponse(data, seedSha256) {
  const nodes = (data.nodes || []).map(n => ({
    ...n,
    id: n.id || n.sha256,
    is_seed: !!(n.sha256 === seedSha256 || n.id === seedSha256 || n.is_seed),
  }));

  // Group per-type edge rows into one edge per (source, target) pair
  const edgeMap = new Map();
  (data.edges || []).forEach(e => {
    const pairKey = [e.source, e.target].sort().join('|');
    if (!edgeMap.has(pairKey)) {
      edgeMap.set(pairKey, { source: e.source, target: e.target, dimensions: [] });
    }
    const edge = edgeMap.get(pairKey);
    const type = DIM_NAME_REVERSE_MAP[e.type] || e.type;
    const existing = edge.dimensions.find(d => d.type === type);
    const vals = e.values || (e.value ? [e.value] : []);
    if (existing) {
      existing.count = Math.max(existing.count, e.count ?? 1);
      vals.forEach(v => { if (!existing.shared_values.includes(v)) existing.shared_values.push(v); });
    } else {
      edge.dimensions.push({ type, count: e.count ?? 1, shared_values: [...vals] });
    }
  });

  const edges = [...edgeMap.values()].map(e => ({
    ...e,
    total_shared_dimensions: e.dimensions.length,
    total_shared_values: e.dimensions.reduce((s, d) => s + d.count, 0),
  }));

  return { ...data, nodes, edges };
}

class ApiService {
  constructor() {
    console.log('URL from build-time config:', API_BASE_URL);
    this.baseUrl = API_BASE_URL;
  }

  async getCurrentUser() {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No token found');
  }

  const response = await fetch(`${this.baseUrl}/auth/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    throw new Error('Failed to fetch user information');
  }

  return response.json();
  }

  getHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  }

  async handleResponse(response) {
    if (response.ok) {
      return response.json();
    }
    
    // Handle authentication errors (401 Unauthorized)
    if (response.status === 401) {
      // Clear the invalid token
      localStorage.removeItem('token');
      
      // Redirect to login page
      window.location.href = '/login';
      
      throw new Error('Your session has expired. Please log in again.');
    }

    //(500 Generic Error)
    if (response.status === 500) {
        // Clear the invalid token
        localStorage.removeItem('token');
        
        // Redirect to login page
        window.location.href = '/login';
        
        throw new Error('Your session has expired. Please log in again.');
      }


    
    // Handle other errors
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  async get(endpoint) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async post(endpoint, data) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  async delete(endpoint) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async patch(endpoint, data) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async listAnalyses(page = 1, pageSize = 10, filters = {}) {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, String(v)));
      } else if (value !== '' && value !== null && value !== undefined) {
        params.append(key, String(value));
      }
    }
    return this.get(`/search/list?${params.toString()}`);
  }

  async searchAnalyses(query, page = 1, pageSize = 10) {
    return this.post(`/search/analysis?page=${page}&page_size=${pageSize}`, { query });
  }

  async getPredefinedTags() {
    return this.get('/files/tags/predefined');
  }

  async updateTags(sha256, add = [], remove = []) {
    return this.patch(`/files/analysis/${sha256}/tags`, { add, remove });
  }

  async updateFamily(sha256, family) {
    return this.patch(`/files/analysis/${sha256}/family`, { malware_family: family });
  }

  async getComments(sha256) {
    return this.get(`/files/analysis/${sha256}/comments`);
  }

  async addComment(sha256, text) {
    return this.post(`/files/analysis/${sha256}/comments`, { text });
  }

  async generateApiKey(label) {
    return this.post('/auth/api-keys', { label });
  }

  async listApiKeys() {
    return this.get('/auth/api-keys');
  }

  async revokeApiKey(keyId) {
    return this.delete(`/auth/api-keys/${keyId}`);
  }

  async listAdminUsers() {
    return this.get('/admin/users');
  }

  async createAdminUser(email, password, isAdmin = false) {
    return this.post('/admin/users', { email, password, is_admin: isAdmin });
  }

  async activateUser(email) {
    return this.patch(`/admin/users/${encodeURIComponent(email)}/activate`, {});
  }

  async deactivateUser(email) {
    return this.patch(`/admin/users/${encodeURIComponent(email)}/deactivate`, {});
  }

  async deleteAdminUser(email) {
    return this.delete(`/admin/users/${encodeURIComponent(email)}`);
  }

  async resetUserPassword(email, newPassword) {
    return this.post(`/admin/users/${encodeURIComponent(email)}/reset-password`, { new_password: newPassword });
  }

  async getComponentCounts(sha256) {
    return this.get(`/pivots/${sha256}/component-counts`);
  }

  async pivotByService(serviceName, limit = 100, excludeSha256 = null) {
    const params = new URLSearchParams({ limit });
    if (excludeSha256) params.append('exclude_sha256', excludeSha256);
    return this.get(`/pivots/service/${encodeURIComponent(serviceName)}?${params}`);
  }

  async pivotByReceiver(receiverName, limit = 100, excludeSha256 = null) {
    const params = new URLSearchParams({ limit });
    if (excludeSha256) params.append('exclude_sha256', excludeSha256);
    return this.get(`/pivots/receiver/${encodeURIComponent(receiverName)}?${params}`);
  }

  async pivotByIntentAction(intentAction, limit = 100, excludeSha256 = null) {
    const params = new URLSearchParams({ limit });
    if (excludeSha256) params.append('exclude_sha256', excludeSha256);
    return this.get(`/pivots/intent-action/${encodeURIComponent(intentAction)}?${params}`);
  }

  async pivotByIconHash(hash, limit = 20) {
    return this.get(`/pivots/icon-hash/${encodeURIComponent(hash)}?limit=${limit}`);
  }

  async pivotByMalwareFamily(family, limit = 20) {
    return this.get(`/pivots/malware-family/${encodeURIComponent(family)}?limit=${limit}`);
  }

  async pivotByArtifact(artifactSha256, limit = 20) {
    return this.get(`/pivots/artifact/${encodeURIComponent(artifactSha256)}?limit=${limit}`);
  }

  async submitToSandbox(sha256) {
    return this.post(`/dynamic-analysis/${sha256}/submit`, {});
  }

  async buildInvestigationGraph(sha256, pivotTypes, limitPerPivot = 20, dateFilters = {}) {
    return this.post('/investigation/graph', {
      sha256,
      pivot_types: pivotTypes,
      limit_per_pivot: limitPerPivot,
      ...dateFilters,
    });
  }

  async buildGraph(sha256, dimensions, resultsPerPivot = 20, dateFilters = {}) {
    const data = await this.post('/investigation/graph', {
      sha256,
      pivot_types: mapDimensions(dimensions),
      limit_per_pivot: resultsPerPivot,
      ...dateFilters,
    });
    return normalizeGraphResponse(data, sha256);
  }

  async expandGraphNode(nodeSha256, existingNodeIds, dimensions, resultsPerPivot = 20, dateFilters = {}) {
    const data = await this.post('/investigation/graph', {
      sha256: nodeSha256,
      pivot_types: mapDimensions(dimensions),
      limit_per_pivot: resultsPerPivot,
      ...dateFilters,
    });
    return normalizeGraphResponse(data, null);
  }

  async resolveSeed(seedType, seedValue, limit = 20) {
    let data;
    if (seedType === 'service')            data = await this.pivotByService(seedValue, limit, null);
    else if (seedType === 'receiver')      data = await this.pivotByReceiver(seedValue, limit, null);
    else if (seedType === 'intent_action') data = await this.pivotByIntentAction(seedValue, limit, null);
    else if (seedType === 'icon_hash')     data = await this.pivotByIconHash(seedValue, limit);
    else if (seedType === 'malware_family') data = await this.pivotByMalwareFamily(seedValue, limit);
    else if (seedType === 'artifact')      data = await this.pivotByArtifact(seedValue, limit);
    else {
      // certificate, cert_serial, permission_set — new endpoint when backend supports it
      data = await this.post('/investigations/resolve-seed', { seed_type: seedType, seed_value: seedValue, limit });
    }
    const results = (data?.results || []).map(r => ({ ...r, id: r.id || r.sha256 }));
    return { results };
  }

  async getDashboardStats() {
    return this.get('/dashboard/stats');
  }

  async getHealthCheck() {
    const response = await fetch(`${this.baseUrl}/dashboard/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return response.json();
  }
}

export const apiService = new ApiService();